import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Flex,
  Text,
  Tooltip,
  Button,
  DropdownMenu,
  Box,
} from '@radix-ui/themes';
import { useTranslation } from 'react-i18next';
import { useQueryClient, useMutation } from '@tanstack/react-query';

import { BROADCAST_STATUS_TYPES, BROADCAST_DOT_COLORS } from './helpers.js';
import { ScheduleModal } from '../../../../broadcastMessages/components/ScheduleModal.jsx';
import { SaveDraftButton } from '../../../../broadcastMessages/components/SaveDraftButton.jsx';
import { ConfirmationModal } from '../../../../broadcastMessages/components/ConfirmationModal.jsx';
import {
  useRescheduleMutation,
  useUpdateDraftDateMutation,
} from '../../../../broadcastMessages/hooks/useBroadcastMessagesMutation';
import {
  deleteBroadcastMessage,
  cancelScheduledBroadcast,
} from '../../../../broadcastMessages/hooks/data';
import { useNotification } from '../../../../../shared/Notification/useNotification';
import styles from './CalendarView.module.css';

export default function BroadcastItem({
  broadcast,
  hoveredBroadcast,
  onHover,
  onClick,
  isModalOrDropdownClick,
}) {
  const { t } = useTranslation('broadcasts');
  const queryClient = useQueryClient();
  const { showSuccess } = useNotification();
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showChangeDateModal, setShowChangeDateModal] = useState(false);

  const { mutate: deleteBroadcast } = useMutation(deleteBroadcastMessage, {
    onSuccess: () => {
      queryClient.invalidateQueries(['campaigns-by-year']);
      queryClient.invalidateQueries(['broadcasts-by-year']);
      showSuccess({ content: t('successDelete') });
      setShowDeleteModal(false);
    },
  });

  const { updateDraftDateMutation } = useUpdateDraftDateMutation();

  const { mutate: cancelScheduled } = useMutation(cancelScheduledBroadcast, {
    onSuccess: () => {
      queryClient.invalidateQueries(['campaigns-by-year']);
      queryClient.invalidateQueries(['broadcasts-by-year']);
      showSuccess({ content: t('scheduling.cancelSuccess') });
    },
  });

  const status = broadcast.status || BROADCAST_STATUS_TYPES.SCHEDULED;
  const isScheduled = status === BROADCAST_STATUS_TYPES.SCHEDULED;
  const isDraft = status === BROADCAST_STATUS_TYPES.DRAFT;

  const contentPreview = broadcast.contentPreview || '';
  const isTruncated = contentPreview.length > 30;
  const truncatedContent = isTruncated
    ? `${contentPreview.slice(0, 27)}…`
    : contentPreview;

  const ariaLabel = `${broadcast.broadcastTypeLabel} broadcast (${t(
    `calendar.${status}`,
  )} status)`;
  const formattedDate = broadcast.formattedDate || '';

  const { rescheduleMutation } = useRescheduleMutation({
    onSuccessCallback: () => {
      setShowScheduleModal(false);
      queryClient.invalidateQueries(['campaigns-by-year']);
      queryClient.invalidateQueries(['broadcasts-by-year']);
    },
  });

  const handleEditSchedule = e => {
    e.stopPropagation();
    e.preventDefault();
    setShowScheduleModal(true);
  };

  const handleReschedule = ({ scheduledDate, scheduledHour }) => {
    rescheduleMutation({
      id: broadcast.uuid,
      scheduledDate,
      scheduledHour,
    });
  };

  const handleChangeDateClick = e => {
    e.stopPropagation();
    e.preventDefault();
    setShowChangeDateModal(true);
  };

  const handleChangeDate = selectedDate => {
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    updateDraftDateMutation({
      id: broadcast.uuid,
      draftDate: `${year}-${month}-${day}`,
    });
  };

  const handleDeleteClick = e => {
    e.stopPropagation();
    e.preventDefault();
    setShowDeleteModal(true);
  };

  const handleCancelSchedule = e => {
    e.stopPropagation();
    e.preventDefault();
    cancelScheduled({ id: broadcast.uuid });
  };

  const handleConfirmDelete = () => {
    deleteBroadcast({ id: broadcast.uuid });
  };

  const getBroadcastTypeIcon = () => 'fa-regular fa-paper-plane';

  const renderBroadcastStatus = () => {
    if (isScheduled) {
      return (
        <Flex align="center" gap="2">
          <Tooltip content={t('calendar.editSchedule')}>
            <Button
              variant="soft"
              size="1"
              className={`${styles.dateButton} ${
                hoveredBroadcast === broadcast.uuid ? styles.hovered : ''
              }`}
              onClick={handleEditSchedule}
            >
              {formattedDate}
            </Button>
          </Tooltip>
          <Tooltip content={t('calendar.moreOptions')}>
            <Box className={styles.rowActionContainer}>
              <DropdownMenu.Root>
                <DropdownMenu.Trigger>
                  <div
                    className={styles.ellipsisButton}
                    onClick={e => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                  >
                    <i className="fa-regular fa-ellipsis-v fa-2x" />
                  </div>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content>
                  <DropdownMenu.Item onClick={handleCancelSchedule}>
                    <i
                      className="fa-regular fa-calendar-xmark"
                      style={{ marginRight: '8px' }}
                    />
                    {t('calendar.cancelSchedule')}
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            </Box>
          </Tooltip>
        </Flex>
      );
    }

    if (isDraft) {
      return (
        <Flex align="center" gap="2">
          <Tooltip content={t('singleBroadcast.changeDate')}>
            <Button
              variant="soft"
              size="1"
              className={`${styles.dateButton} ${
                hoveredBroadcast === broadcast.uuid ? styles.hovered : ''
              }`}
              onClick={handleChangeDateClick}
            >
              {formattedDate}
            </Button>
          </Tooltip>
          <Tooltip content={t('calendar.moreOptions')}>
            <Box className={styles.rowActionContainer}>
              <DropdownMenu.Root>
                <DropdownMenu.Trigger>
                  <div
                    className={styles.ellipsisButton}
                    onClick={e => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                  >
                    <i className="fa-regular fa-ellipsis-v fa-2x" />
                  </div>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content>
                  <DropdownMenu.Item onClick={handleDeleteClick}>
                    <i
                      className="fa-regular fa-trash"
                      style={{ marginRight: '8px' }}
                    />
                    {t('calendar.delete')}
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            </Box>
          </Tooltip>
        </Flex>
      );
    }

    // Sent / Error — date only, no actions
    return (
      <Flex align="center" gap="2">
        <Text
          size="1"
          className={`${styles.dateText} ${styles.broadcastDateText}`}
        >
          {formattedDate}
        </Text>
      </Flex>
    );
  };

  return (
    <>
      <Flex
        className={styles.campaignItem}
        style={{ borderLeft: `2px solid ${BROADCAST_DOT_COLORS[status]}` }}
        align="center"
        justify="between"
        as="li"
        tabIndex={0}
        aria-label={ariaLabel}
        onMouseEnter={() => onHover(broadcast.uuid, true)}
        onMouseLeave={() => onHover(broadcast.uuid, false)}
        onClick={e => {
          if (isModalOrDropdownClick(e)) {
            e.stopPropagation();
            return;
          }
          onClick(broadcast);
        }}
      >
        <Flex className={styles.campaignContentContainer} align="center">
          <Tooltip
            content={`${t(`calendar.${status}`)}: ${
              broadcast.broadcastTypeLabel
            }`}
          >
            <Flex align="center" gap="2">
              <span
                className={styles.campaignDot}
                style={{ background: BROADCAST_DOT_COLORS[status] }}
                aria-label={`${t(`calendar.${status}`)} status`}
              />
              <i
                className={`${getBroadcastTypeIcon()} ${styles.broadcastIcon}`}
              />
            </Flex>
          </Tooltip>
          {isTruncated ? (
            <Tooltip content={contentPreview}>
              <Text size="1" className={styles.campaignNameText}>
                {truncatedContent}
              </Text>
            </Tooltip>
          ) : (
            <Text size="1" className={styles.campaignNameText}>
              {contentPreview}
            </Text>
          )}
        </Flex>
        {renderBroadcastStatus()}
      </Flex>

      <ScheduleModal
        open={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        onSchedule={handleReschedule}
        isEdit
      />

      <ConfirmationModal
        header={t('deleteConfirmationModal.header')}
        body={t('deleteConfirmationModal.body')}
        cancelText={t('deleteConfirmationModal.cancel')}
        submitText={t('deleteConfirmationModal.submit')}
        open={showDeleteModal}
        variant="error"
        onClose={() => setShowDeleteModal(false)}
        onSave={handleConfirmDelete}
      />

      <SaveDraftButton
        open={showChangeDateModal}
        onClose={() => setShowChangeDateModal(false)}
        onSave={handleChangeDate}
      />
    </>
  );
}

BroadcastItem.propTypes = {
  broadcast: PropTypes.shape({
    uuid: PropTypes.string,
    contentPreview: PropTypes.string,
    displayDate: PropTypes.string,
    formattedDate: PropTypes.string,
    status: PropTypes.string,
    broadcastType: PropTypes.string,
    broadcastTypeLabel: PropTypes.string,
    itemType: PropTypes.string,
  }),
  hoveredBroadcast: PropTypes.string,
  onHover: PropTypes.func,
  onClick: PropTypes.func,
  isModalOrDropdownClick: PropTypes.func,
};
